import type { InputDOMProps } from "@react-types/shared";
import { clsx } from "clsx";
import {
  type CSSProperties,
  forwardRef,
  type ForwardRefRenderFunction,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { type AriaFieldProps, useFocusWithin, VisuallyHidden } from "react-aria";
import {
  Badge,
  Description,
  FormError,
  HStack,
  Label,
  NumberFormatField,
  Spacer,
  SymbolIcon,
  TriggerButton,
  TriggerButtonSize,
} from "widgets";
import formStyle from "widgets/form.module.css";
import { useDataAttributes, useDOMRef, useResizeObserver } from "widgets/hooks";

import type { ContigInfoFragment } from "@/modules/entities/graphql/contig-queries.gql.ts";
import { validateRegion } from "@/modules/entities/utils/validateRegion.ts";

import style from "../styles/RegionField.module.css";
import { RegionFieldContigComboBox } from "./RegionFieldContigComboBox.tsx";

export interface RegionFieldProps extends AriaFieldProps, InputDOMProps {
  style?: CSSProperties;
  genomeIds?: number[] | null;
  autoFocus?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  placeholder?: string;

  /** Because JavaScript is fun, `start` and `stop` can be `NaN` */
  onChange?: (contigId: number | null, start: number, stop: number) => void;

  defaultValue?: { contigId: number; start?: number | null; stop?: number | null };
  extraAction?: ReactElement;

  // onInternalError
  onBlurWithin?: () => void;
  onInternalErrorChange?: (error: boolean) => void;
}

const RegionFieldRender: ForwardRefRenderFunction<HTMLInputElement, RegionFieldProps> = (props, ref) => {
  const {
    defaultValue,
    onInternalErrorChange,
    onBlurWithin,
    onChange,
    genomeIds,
    isReadOnly,
    isRequired,
    label,
    placeholder,
    errorMessage,
    extraAction,
    isInvalid,
    autoFocus,
    isDisabled,
    description,
  } = props;
  const [start, setStart] = useState<number>(defaultValue?.start ?? Number.NaN);
  const [stop, setStop] = useState<number>(defaultValue?.stop ?? Number.NaN);
  const [contig, setContig] = useState<ContigInfoFragment | null>(
    defaultValue?.contigId ? ({ id: defaultValue?.contigId } as ContigInfoFragment) : null,
  );
  const [isFocus, onFocusChange] = useState(false);

  // setup of combobox
  const contigSelectorRef = useDOMRef<HTMLInputElement>(ref);
  const stackRef = useRef<HTMLDivElement>(null);
  const defaultValueRef = useRef(defaultValue);

  const rect = useResizeObserver(stackRef);
  const { focusWithinProps } = useFocusWithin({
    onFocusWithinChange(isFocusWithin) {
      onFocusChange(isFocusWithin);

      if (!isFocusWithin) {
        onBlurWithin?.();
      }
    },
  });

  // internal validation
  const [error, setError] = useState<string | null>(null);

  // make the outside aware of the current error state
  useEffect(() => {
    if (onInternalErrorChange) {
      onInternalErrorChange(!!error);
    }
  }, [error, onInternalErrorChange]);

  const disabled = isDisabled || genomeIds == null || genomeIds.length === 0;

  const onContigChange = useCallback((contig: ContigInfoFragment | null) => {
    setContig(contig);

    const currentDefault = defaultValueRef.current;

    if (contig) {
      const shouldUseDefaults = currentDefault && contig.id === currentDefault?.contigId;

      setStart(shouldUseDefaults ? (currentDefault?.start ?? 1) : 1);
      setStop(shouldUseDefaults ? (currentDefault?.stop ?? contig.length) : contig.length);
    } else {
      setStart(Number.NaN);
      setStop(Number.NaN);
    }
  }, []);

  useEffect(() => {
    if (genomeIds == null || genomeIds.length === 0) {
      setContig(null);
      setStart(Number.NaN);
      setStop(Number.NaN);
    }
  }, [genomeIds]);

  useEffect(() => {
    if (!contig) {
      setError(null);
      onChange?.(null, Number.NaN, Number.NaN);
    } else {
      const __start = Number.isNaN(start) ? 1 : start;
      const __stop = Number.isNaN(stop) ? contig.length : stop;
      const [, checkResult] = validateRegion(contig.length, { start: __start, stop: __stop });

      if (checkResult.success) {
        setError(null);
      } else {
        setError(checkResult.error.issues[0].message);
      }
      onChange?.(contig.id, __start, __stop);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contig, start, stop]);

  const dataAttributes = useDataAttributes(props);

  return (
    <div
      style={style}
      ref={stackRef}
      data-testid={`${dataAttributes["data-testid"] ? `${dataAttributes["data-testid"]}__` : ""}region`}
    >
      <div className={formStyle.input_layout}>
        {!!label && (
          <HStack className={formStyle.input_layout_column1} style={{ alignItems: "baseline" }}>
            <Label>{label}</Label>
            <Spacer />
            {!!isReadOnly && <Badge intent="light" large inline label="read only" style={{ flex: "0 0 auto" }} />}
            {!isRequired && !isReadOnly && (
              <Badge intent="light" large inline label="optional" style={{ flex: "0 0 auto" }} />
            )}
          </HStack>
        )}
        <Description
          data-testid="region-field-description"
          className={clsx(formStyle.input_layout_column1, {
            [formStyle.description_with_tag]: label != undefined && (isReadOnly || !isRequired),
          })}
        >
          {description}
        </Description>
        <HStack
          spacing={3}
          domProps={focusWithinProps}
          className={clsx(style.input_group, formStyle.input_layout_column1, {
            [style.disabled]: disabled,
            [style.focus]: isFocus,
            [style.error]: (isInvalid || !!error) && !disabled,
          })}
        >
          {!label && (
            <VisuallyHidden>
              <label>{placeholder}</label>
            </VisuallyHidden>
          )}
          <RegionFieldContigComboBox
            genomeIds={genomeIds}
            onContigChange={onContigChange}
            width={Number(((rect?.width ?? 150) / 2).toFixed(0))}
            popoverWidth={rect?.width}
            ref={contigSelectorRef}
            selectedKey={contig?.id}
            isReadOnly={isReadOnly}
            isRequired={isRequired}
            isDisabled={disabled}
            isInvalid={isInvalid || !!error}
            autoFocus={autoFocus}
            action={
              contig && !disabled ? (
                <TriggerButton
                  icon={<SymbolIcon icon="cancel" filled size={12} style={{ color: "var(--form-placeholder)" }} />}
                  label=""
                  aria-label="Delete"
                  onPress={() => {
                    onContigChange(null);
                  }}
                  size={TriggerButtonSize.Small}
                  style={{ width: 20 }}
                />
              ) : undefined
            }
          />
          <span className={style.separator}>:</span>
          <NumberFormatField
            placeholder="Start"
            thousandSeparator
            onValueChange={(values) => {
              if (values.floatValue != null) {
                setStart(values.floatValue);
              }
            }}
            value={start}
            isDisabled={disabled}
            isReadOnly={isReadOnly}
            isRequired={isRequired}
            noBorder
          />
          <span className={style.separator}>&ndash;</span>
          <NumberFormatField
            placeholder="Stop"
            thousandSeparator
            onValueChange={(values) => {
              if (values.floatValue != null) {
                setStop(values.floatValue);
              }
            }}
            value={stop}
            isDisabled={disabled}
            isReadOnly={isReadOnly}
            isRequired={isRequired}
            noBorder
          />
        </HStack>
        {!!extraAction && <HStack className={formStyle.input_layout_column2}>{extraAction}</HStack>}
        {(!!errorMessage || (!!label && !!error)) && (
          <FormError>
            {errorMessage as ReactNode}
            {!!error && errorMessage ? " " : null}
            {error}
          </FormError>
        )}
      </div>
    </div>
  );
};

export const RegionField = forwardRef(RegionFieldRender);
